import { useState } from "react";
import {
	Workflow,
	MessageSquare,
	Users,
	ChevronLeft,
	ChevronRight,
	Play,
	Eye,
} from "lucide-react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FlowCanvas } from "@/components/flow-canvas";
import { NodePalette } from "@/components/node-palette";
import { NodeConfigPanel } from "@/components/node-config-panel";
import { ChatPanel } from "@/components/chat-panel";
import { LeadsPanel } from "@/components/leads-panel";
import { useFlowStore } from "@/lib/flow-store";
import "./index.css";

type View = "flow" | "chat" | "leads";

const NAV_ITEMS: {
	id: View;
	label: string;
	icon: React.ElementType;
	color: string;
}[] = [
	{
		id: "flow",
		label: "Flow Editor",
		icon: Workflow,
		color: "#6366f1",
	},
	{
		id: "chat",
		label: "Chat Preview",
		icon: MessageSquare,
		color: "#8b5cf6",
	},
	{
		id: "leads",
		label: "Leads",
		icon: Users,
		color: "#ec4899",
	},
];

export function App() {
	const [currentView, setCurrentView] = useState<View>("flow");
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
	const store = useFlowStore();

	return (
		<TooltipProvider>
			<div className="flex h-screen w-screen overflow-hidden bg-[#0d0f14]">
				{/* Sidebar navigation */}
				<aside
					className={`flex flex-col border-r border-white/[0.06] bg-[#0a0c10] transition-all duration-300 shrink-0 ${
						sidebarCollapsed ? "w-[60px]" : "w-[200px]"
					}`}
				>
					{/* Logo */}
					<div className="flex items-center gap-2.5 px-3 py-4 border-b border-white/[0.06]">
						{!sidebarCollapsed && (
							<h1 className="text-sm font-extrabold bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
								ChatConvert
							</h1>
						)}
					</div>

					{/* Nav items */}
					<nav className="flex-1 p-2 space-y-1">
						{NAV_ITEMS.map((item) => {
							const isActive = currentView === item.id;
							return (
								<button
									key={item.id}
									onClick={() => setCurrentView(item.id)}
									className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all text-left ${
										isActive
											? "bg-white/[0.06]"
											: "hover:bg-white/[0.04]"
									}`}
								>
									<div
										className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-transform"
										style={{
											background: isActive
												? `${item.color}20`
												: "transparent",
										}}
									>
										<item.icon
											size={16}
											style={{
												color: isActive
													? item.color
													: undefined,
											}}
											className={
												isActive
													? ""
													: "text-zinc-500"
											}
											strokeWidth={isActive ? 2.5 : 2}
										/>
									</div>
									{!sidebarCollapsed && (
										<span
											className={`text-xs font-medium ${
												isActive
													? "text-zinc-200"
													: "text-zinc-500"
											}`}
										>
											{item.label}
										</span>
									)}
									{!sidebarCollapsed &&
										item.id === "leads" && (
											<Badge
												variant="secondary"
												className="ml-auto text-[9px] px-1.5 py-0 h-4 bg-pink-500/15 text-pink-400 border-0"
											>
												{store.leads.length}
											</Badge>
										)}
								</button>
							);
						})}
					</nav>

					{/* Collapse toggle */}
					<div className="p-2 border-t border-white/[0.06]">
						<button
							onClick={() =>
								setSidebarCollapsed(!sidebarCollapsed)
							}
							className="w-full flex items-center justify-center gap-2 px-2.5 py-2 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06] transition-colors"
						>
							{sidebarCollapsed ? (
								<ChevronRight size={14} />
							) : (
								<>
									<ChevronLeft size={14} />
									<span className="text-[10px]">
										Collapse
									</span>
								</>
							)}
						</button>
					</div>
				</aside>

				{/* Main content */}
				<main className="flex-1 flex flex-col overflow-hidden">
					{/* Top bar */}
					<header className="relative flex items-center justify-between px-4 py-3">
						<div className="flex items-center gap-3">
							<div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/[0.08] backdrop-blur-sm">
								<h2 className="text-sm font-bold text-zinc-200">
									{NAV_ITEMS.find(
										(n) => n.id === currentView
									)?.label}
								</h2>
							</div>
						</div>
						<div className="flex items-center gap-2">
							{currentView === "flow" && (
								<>
									<Button
										variant="outline"
										size="sm"
										className="text-xs h-7 gap-1.5 rounded-full border-white/[0.08] text-zinc-300 hover:bg-white/[0.06] backdrop-blur-sm"
										onClick={() =>
											setCurrentView("chat")
										}
									>
										<Eye size={12} />
										Preview
									</Button>
									<Button
										size="sm"
										className="text-xs h-7 gap-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white shadow-lg shadow-indigo-500/20"
									>
										<Play size={12} />
										Deploy Bot
									</Button>
								</>
							)}
						</div>
					</header>

					{/* View content */}
					<div className="flex-1 overflow-hidden">
						{currentView === "flow" && (
							<FlowEditorView store={store} />
						)}
						{currentView === "chat" && (
							<div className="h-full max-w-lg mx-auto border-x border-white/[0.06] bg-[#0d0f14]">
								<ChatPanel
									messages={store.chatMessages}
									onSendMessage={store.addChatMessage}
								/>
							</div>
						)}
						{currentView === "leads" && (
							<div className="h-full max-w-2xl mx-auto border-x border-white/[0.06] bg-[#0d0f14]">
								<LeadsPanel leads={store.leads} />
							</div>
						)}
					</div>
				</main>
			</div>
		</TooltipProvider>
	);
}

function FlowEditorView({
	store,
}: {
	store: ReturnType<typeof useFlowStore>;
}) {
	return (
		<div className="flex h-full">
			{/* Left: Node palette */}
			<div className="w-[200px] shrink-0 border-r border-white/[0.06] bg-[#0a0c10] p-3 overflow-y-auto">
				<NodePalette />
			</div>

			{/* Center: Canvas */}
			<div className="flex-1 relative">
				<FlowCanvas
					nodes={store.nodes}
					connections={store.connections}
					selectedNodeId={store.selectedNodeId}
					onSelectNode={store.setSelectedNodeId}
					onMoveNode={(id, x, y) =>
						store.updateNode(id, { x, y })
					}
					onDropNewNode={(type, x, y) => store.addNode(type, x, y)}
					onRemoveNode={store.removeNode}
					onConnect={(from, to) =>
						store.addConnection(from, to)
					}
				/>
			</div>

			{/* Right: Config panel */}
			{store.selectedNode && (
				<div className="w-[280px] shrink-0 border-l border-white/[0.06] bg-[#0a0c10] overflow-hidden">
					<NodeConfigPanel
						node={store.selectedNode}
						onUpdate={store.updateNode}
						onRemove={store.removeNode}
						onClose={() => store.setSelectedNodeId(null)}
					/>
				</div>
			)}
		</div>
	);
}

export default App;
